export type ContactFormPayload = {
  cabinet: string;
  usersCount: string;
  nom: string;
  prenom: string;
  email: string;
  telephone: string;
  message: string;
  billingSoftware: string;
};

export type SubscribeFormPayload = {
  prenom: string;
  nom: string;
  fonction: string;
  telephone: string;
  email: string;
  adresse: string;
  siren: string;
  cabinet: string;
};

const CONTACT_ENDPOINT = 'https://zlawyercontact.azurewebsites.net/api/contact';

export async function getRecaptchaToken(siteKey: string): Promise<string> {
  if (typeof window === 'undefined') {
    throw new Error("reCAPTCHA n'est pas disponible côté serveur.");
  }

  const { grecaptcha } = window;
  if (!grecaptcha) throw new Error("reCAPTCHA n'est pas encore chargé.");

  return new Promise<string>((resolve, reject) => {
    let isSettled = false;

    const timeoutId = window.setTimeout(() => {
      if (isSettled) return;
      isSettled = true;
      reject(new Error('Le service reCAPTCHA met trop de temps à répondre.'));
    }, 10000);

    const settleResolve = (token: string) => {
      if (isSettled) return;
      isSettled = true;
      window.clearTimeout(timeoutId);
      resolve(token);
    };

    const settleReject = (message: string) => {
      if (isSettled) return;
      isSettled = true;
      window.clearTimeout(timeoutId);
      reject(new Error(message));
    };

    try {
      grecaptcha.ready(() => {
        grecaptcha
          .execute(siteKey, { action: 'contact_form' })
          .then((token) => {
            if (!token) {
              settleReject("Impossible d'obtenir le token reCAPTCHA.");
              return;
            }
            settleResolve(token);
          })
          .catch(() => settleReject('Une erreur est survenue avec reCAPTCHA.'));
      });
    } catch {
      settleReject("reCAPTCHA n'a pas pu être initialisé.");
    }
  });
}

async function postToContactEndpoint(
  payload: ContactFormPayload | SubscribeFormPayload,
  recaptchaToken: string,
): Promise<void> {
  const response = await window.fetch(CONTACT_ENDPOINT, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ...payload, recaptchaToken }),
  });

  const responseText = await response.text();

  if (!response.ok) {
    throw new Error(responseText || "Erreur lors de l'envoi du formulaire.");
  }
}

export const submitContactForm = (form: ContactFormPayload, recaptchaToken: string) =>
  postToContactEndpoint(form, recaptchaToken);

export const submitSubscribeForm = (form: SubscribeFormPayload, recaptchaToken: string) =>
  postToContactEndpoint(form, recaptchaToken);
